// flow
import { createSelector } from "reselect"
import * as R from "ramda"

import { isKiwiBranding } from "../utils/isKiwiBranding"
import { iGet } from "../../utils/immutable"

const optionsSelector = state => state.options

export const iGetIfEnabled = prop => x => (x.get("enabled") ? x.get(prop) : "")

const getPropertyDeep = (property, valueToGet) => options =>
  options.getIn(["branding", "contacts", property, "enabled"])
    ? options.getIn([
        "branding",
        "contacts",
        property,
        "locales",
        options.get("language"),
        valueToGet,
      ]) || options.getIn(["branding", "contacts", property, "locales", "default", valueToGet])
    : ""

const prependPlus = x => (x ? `+${x}` : x)

// SELECTORS
export const brandingSelector = createSelector(
  optionsSelector,
  x => x.branding,
)
export const brandingIdSelector = createSelector(
  brandingSelector,
  iGet("id"),
)
export const brandingNameSelector = createSelector(
  brandingSelector,
  iGet("name"),
)
export const brandingCompanyNameSelector = createSelector(
  brandingSelector,
  iGet("company_name"),
)
export const bookedAtSelector = createSelector(
  brandingSelector,
  iGet("booked_at"),
)
export const poweredByKiwiSelector: () => boolean = createSelector(
  brandingSelector,
  iGet("powered_by_kiwi"),
)

const phoneFromOptions = R.compose(
  prependPlus,
  getPropertyDeep("phones", "number"),
)
const emailFromOptions = getPropertyDeep("emails", "email")

export const homeRedirectUrlSelector: () => string = createSelector(
  brandingSelector,
  iGet("home_redirect_url"),
)
export const support247Selector: () => boolean = createSelector(
  brandingSelector,
  iGet("support_247"),
)

// CONTACTS
export const supportPhoneSelector: () => string = createSelector(
  optionsSelector,
  phoneFromOptions,
)
export const supportEmailSelector: () => string = createSelector(
  optionsSelector,
  emailFromOptions,
)

export const isChatEnabledSelector: () => boolean = createSelector(
  brandingSelector,
  () => false,
)
export const isPhoneEnabledSelector: () => boolean = createSelector(
  brandingSelector,
  iGet(["contacts", "phones", "enabled"]),
)
export const getPhones: () => boolean = createSelector(
  brandingSelector,
  iGet(["contacts", "phones", "locales"]),
)

// COMMUNICATION
export const isNewsletterEnabledSelector: () => boolean = createSelector(
  brandingSelector,
  iGet(["communication", "newsletter", "enabled"]),
)

// AUTH
export const isCredentialsEnabledSelector: () => boolean = createSelector(
  brandingSelector,
  iGet(["auth", "credentials"]),
)
export const isFacebookEnabledSelector: () => boolean = createSelector(
  brandingSelector,
  iGet(["auth", "social_facebook", "enabled"]),
)
export const isGoogleEnabledSelector: () => boolean = createSelector(
  brandingSelector,
  iGet(["auth", "social_google", "enabled"]),
)

// PAYMENTS
export const paymentMethodsSelector = createSelector(
  brandingSelector,
  iGet(["payments", "payment_methods"]),
)

export const brandingEnabledPaymentMethodsSelector = createSelector(
  paymentMethodsSelector,
  methods =>
    methods
      .filter(x => x.get("enabled"))
      .keySeq()
      .toArray(),
)
export const zoozIdSelector = createSelector(
  brandingSelector,
  iGet(["payments", "zooz_id"]),
)

export const isCreditsAllowed = createSelector(
  brandingSelector,
  branding => branding.get("id") === "kiwicom", // TODO: get value from config after BE implements possibility to edit in nest
)

export const isKiwiBrandingSelector = createSelector(
  brandingSelector,
  branding => isKiwiBranding(branding),
)

export const socialLinksSelector = createSelector(
  brandingSelector,
  branding =>
    branding
      .getIn(["content", "media"])
      .filter(iGet("enabled"))
      .toJS(),
)

// CONTENT
export const isTrustpilotEnabledSelector = createSelector(
  brandingSelector,
  iGet(["content", "general", "trustpilot", "enabled"]),
)

export const termsTranslationKeySelector = createSelector(
  brandingSelector,
  iGet(["content", "legal", "terms", "t_key"]),
)
export const cookiesTranslationKeySelector = createSelector(
  brandingSelector,
  iGet(["content", "legal", "cookies", "t_key"]),
)
export const privacyTranslationKeySelector = createSelector(
  brandingSelector,
  iGet(["content", "legal", "privacy", "t_key"]),
)
export const bookingAgreementTranslationKeySelector = createSelector(
  brandingSelector,
  iGet(["content", "legal", "booking_agreement", "t_key"]),
)

export const searchModesSelector = createSelector(
  brandingSelector,
  iGet(["content", "search", "modes"]),
)

export const mobileAppsSelector = createSelector(
  brandingSelector,
  iGet(["content", "promotion", "mobile_apps"]),
)

// PAGES/LINKS
export const isTopRoutesEnabledSelector = createSelector(
  brandingSelector,
  iGet(["content", "pages", "top_routes", "enabled"]),
)

export const isMediaEnabledSelector = createSelector(
  brandingSelector,
  iGet(["content", "pages", "media", "enabled"]),
)

const termsSelector = createSelector(
  brandingSelector,
  iGet(["content", "pages", "terms"]),
)
export const termsUrlSelector = createSelector(
  termsSelector,
  iGetIfEnabled("link"),
)

export const helpcenterApiUrlSelector = createSelector(
  brandingSelector,
  iGet(["api", "helpcenter"]),
)

// THEME
export const brandThemeSelector = createSelector(
  brandingSelector,
  iGet(["theme"]),
)
export const brandThemeColorsSelector = createSelector(
  brandingSelector,
  iGet(["theme", "colors"]),
)
